import React, { useCallback, useState, useEffect, useRef } from 'react';
import { TextField, CircularProgress } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useFormContext, Controller } from 'react-hook-form';
import { AddressData } from '../../../types/address';

interface AddressAutocompleteProps {
  disabled?: boolean;
  name?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  onChange?: (data: AddressData) => void;
  preserveZip?: boolean;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  disabled = false,
  name = 'address',
  label = 'Street Address',
  placeholder = 'Enter your address (US only)',
  required = false,
  onChange
}) => {
  const { control, setValue, trigger, getValues } = useFormContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  
  const extractZipFromFormattedAddress = (formattedAddress: string): string => {
    const zipPattern = /\b\d{5}(-\d{4})?\b/;
    const match = formattedAddress.match(zipPattern);
    return match ? match[0] : '';
  };

  const setupAutocomplete = useCallback((input: HTMLInputElement | null) => {
    if (!input || autocompleteRef.current) return;

    try {
      autocompleteRef.current = new google.maps.places.Autocomplete(input, {
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address', 'name', 'geometry'],
        types: ['address']
      });

      autocompleteRef.current.addListener('place_changed', () => {
        if (!autocompleteRef.current) return;
        
        setIsLoading(true);
        const place = autocompleteRef.current.getPlace();
        
        if (place) {
          let streetNumber = '';
          let route = '';
          let city = '';
          let state = '';
          let zipCode = '';
          let hasPostalCode = false;
          
          if (place.address_components && place.address_components.length > 0) {
            for (const component of place.address_components) {
              const types = component.types;
              
              if (types.includes('street_number')) {
                streetNumber = component.long_name;
              } else if (types.includes('route')) {
                route = component.long_name;
              } else if (types.includes('locality')) {
                city = component.long_name;
              } else if (types.includes('sublocality') && !city) {
                city = component.long_name;
              } else if (types.includes('neighborhood') && !city) {
                city = component.long_name;
              } else if (types.includes('administrative_area_level_3') && !city) {
                city = component.long_name;
              } else if (types.includes('administrative_area_level_1')) {
                state = component.short_name;
              } else if (types.includes('postal_code')) {
                zipCode = component.long_name;
                hasPostalCode = true;
              }
            }
          }
       
          const street = streetNumber && route ? `${streetNumber} ${route}` : route || place.name || '';
          const address = place.formatted_address || street;
          if (!hasPostalCode && place.formatted_address) {
            const extractedZip = extractZipFromFormattedAddress(place.formatted_address);
            if (extractedZip) {
              zipCode = extractedZip;
              hasPostalCode = true;
            }
          }
          
          if (!city && place.formatted_address) {
            const addressParts = place.formatted_address.split(',');
            if (addressParts.length > 1) {
              const potentialCity = addressParts[1].trim();
              if (potentialCity && !potentialCity.match(/^[A-Z]{2}$/)) {
                city = potentialCity;
              }
            }
          }
          
          const addressData: AddressData = {
            address,
            street,
            city,
            state,
            zip: zipCode
          };
          
          setTimeout(() => {
            setValue('address', address, { shouldValidate: true, shouldDirty: true });
            setValue('street', street, { shouldValidate: true, shouldDirty: true })
            setValue('city', city, { shouldValidate: true, shouldDirty: true });
            setValue('state', state, { shouldValidate: true, shouldDirty: true });
            setValue('zip', zipCode, { shouldValidate: true, shouldDirty: true });
            
            trigger(['address', 'street', 'city', 'state', 'zip']);
            
            if (onChange) {
              onChange(addressData);
            }
          }, 0);
        }
        
        setIsLoading(false);
      });
    } catch (error) {
      console.error('Error initializing Google Maps Autocomplete:', error);
    }
  }, [setValue, trigger, onChange, getValues, extractZipFromFormattedAddress]);

  useEffect(() => {
    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          fullWidth
          label={label}
          placeholder={placeholder}
          required={required}
          variant="outlined"
          {...field}
          value={field.value || ''} 
          inputRef={setupAutocomplete}
          slotProps={{
            input: {
                startAdornment: <LocationOnIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: isLoading ? <CircularProgress color="inherit" size={20} /> : null   
            }
          }}
          disabled={disabled}
          error={Boolean(error)}
          helperText={error?.message?.toString()}
          onKeyUp={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        />
      )}
    />
  );
};

export default AddressAutocomplete;